import {
  ToGameAppServer,
  ToGameAppClient,
  ExerciseSet,
  ExerciseSetReport,
  Status
} from '../proto-gen-ref/gameapp_pb';
import { ToServer, ToClient, LoginRequest, ResetAck } from '../proto-gen-ref/base_pb';
import { ToAudioServer, ToAudioClient } from '../proto-gen-ref/audio_pb';
import { WebSocketManager } from './WebSocketManager';
import { v4 as uuidv4 } from 'uuid';
import { IGameStreamingService, Settings, IAudioManager } from './types';
import { RealtimeAudioStreamingService } from './RealtimeApiAudioManager';
import { ToastPluginApi } from 'vue-toast-notification';
import type { Meeting, MeetingRoute } from './types';
import { useAuthStore } from './stores/auth';
import { useFeatureFlagsStore } from './stores/featureFlags';
import { AutomabService } from './services/AutomabService';

interface ResponseSettlers {
  resolve: (value: ToClient) => void;
  reject: (reason?: any) => void;
  timeout: ReturnType<typeof setTimeout>;
}

// Constants
const TIMEOUT_DURATION = 10000;
const ERROR_MESSAGES = {
  NO_CREQ: 'sendAndWaitForResponse received non-Creq tgs object',
  RESPONSE_TIMEOUT: 'Response timeout',
  LOGIN_FAILED: 'Login failed',
} as const;

export class GameStreamingService extends WebSocketManager implements IGameStreamingService {
  private responseSettlers = new Map<string, ResponseSettlers>();
  private loginCreqId: string | null = null;
  private settings?: Settings;
  private stringMessageCallbacks: Array<(pushCase: ToGameAppClient.TypeCase, message: string) => void> = [];
  private sessionDataCallbacks: Array<(data: string) => void> = [];
  private audioService: IAudioManager | null = null;

  private constructor(
    wsPathname: string,
    toast: ToastPluginApi,
    userToken: string
  ) {
    super(wsPathname, true, toast, userToken);
  }

  private static instance: GameStreamingService | null = null;
  public static getInstance(): GameStreamingService {
    if (!this.instance) {
      throw new Error('GameStreamingService not initialized');
    }
    return this.instance;
  }

  public static initialize(toast: ToastPluginApi, userToken: string): GameStreamingService {
    const path = '/ws/game';
    if (this.instance) {
      console.error('GameStreamingService already initialized');
    }
    this.instance = new GameStreamingService(path, toast, userToken);
    return this.instance;
  }

  public static reset(): void {
    if (this.instance) {
      this.instance.disconnect();
      this.instance = null;
    }
  }

  public getConnectionId(): number {
    return this.connectionCounter;
  }

  // Store audioConnKey for external access
  private audioConnKey: string | null = null;

  public getAudioConnKey(): string | null {
    return this.audioConnKey;
  }

  /**
   * Send audio message through multiplexed connection
   */
  public sendAudioMessage(msg: ToAudioServer): void {
    const toServer = new ToServer().setAudioMsg(msg);
    const binaryData = toServer.serializeBinary();
    this.wsConnection?.send(binaryData);
  }

  setToken(token: string): void {
    this.userToken = token;
    const savedSettings = localStorage.getItem('settings');
    if (savedSettings) {
      this.settings = JSON.parse(savedSettings);
      console.log('[GameManager] Loaded settings from localStorage');
    } else {
      console.warn('[GameManager] No settings found in localStorage');
    }
  }

  private getAndRemoveSettlers(creqId: string): Omit<ResponseSettlers, 'timeout'> | undefined {
    const settlers = this.responseSettlers.get(creqId);
    if (settlers) {
      clearTimeout(settlers.timeout);
      this.responseSettlers.delete(creqId);
      const { resolve, reject } = settlers;
      return { resolve, reject };
    }
    return undefined;
  }

  private setupRequest(creqId: string): Promise<ToClient> {
    console.log('[GameManager] setupRequest', creqId);

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        if (this.responseSettlers.has(creqId)) {
          console.warn('Request timed out', creqId);
          this.getAndRemoveSettlers(creqId);
          reject(new Error(ERROR_MESSAGES.RESPONSE_TIMEOUT));
        }
      }, TIMEOUT_DURATION);

      this.responseSettlers.set(creqId, { resolve, reject, timeout });
    });
  }

  async sendAndWaitForResponse(): Promise<ResetAck> {
    if (!this.isConnected()) {
      await this.connect();
    }

    const creqId = uuidv4();
    console.log('[GameManager] sendAndWaitForResponse', creqId);

    const responsePromise = this.setupRequest(creqId);
    const toServer = new ToServer().setReset(creqId);
    this.wsConnection!.send(toServer.serializeBinary());
    const response = await responsePromise;
    return response.getResetAck()!;
  }

  protected sendConnectRequest(): void {
    const creqId = uuidv4();
    this.loginCreqId = creqId;
    console.log('[GameManager] sendConnectRequest', creqId);
    const loginReq = new LoginRequest()
      .setCreqId(creqId)
      .setToken(this.userToken)
      .setClientType('aitrainer')
      .setClientAppName('aitrainer');

    if (this.settings) {
      loginReq.setOpenaiApiKey(this.settings.openAiApiKey);
      loginReq.setChatModel(this.settings.chatModel);
      loginReq.setRealtimeModel(this.settings.realtimeModel);
      console.log('[GameManager] Including settings in login request');
    } else {
      console.warn('[GameManager] No settings available for login request');
    }

    this.setupRequest(creqId);
    const toServer = new ToServer().setLogin(loginReq);
    this.wsConnection?.send(toServer.serializeBinary());
  }

  protected handleMessage(event: MessageEvent): void {
    console.log('[GameManager] handleMessage');

    // Deserialize base protocol wrapper
    const toClient = ToClient.deserializeBinary(new Uint8Array(event.data));

    // Handle LOGIN_ACK at base protocol level
    if (toClient.hasLoginAck()) {
      this.handleLoginAckBase(toClient);
      return;
    }

    // Handle RESET_ACK at base protocol level
    if (toClient.hasResetAck()) {
      this.handleResetAckBase(toClient);
      return;
    }

    // Route audio messages to audio service
    if (toClient.hasAudioMsg()) {
      const audioMsg = toClient.getAudioMsg();
      if (audioMsg && this.audioService) {
        this.audioService.handleIncomingMessage(audioMsg);
      }
      return;
    }

    // Handle game messages
    if (!toClient.hasGameMsg()) {
      console.warn('[GameManager] Received ToClient without game or audio message');
      return;
    }

    const tgc = toClient.getGameMsg();
    if (!tgc) {
      console.warn('[GameManager] ToClient.gameMsg is null');
      return;
    }

    const typeCase: ToGameAppClient.TypeCase = tgc.getTypeCase();

    const handlers: Record<ToGameAppClient.TypeCase, () => Promise<void>> = {
      [ToGameAppClient.TypeCase.TYPE_NOT_SET]: () => Promise.resolve(),
      [ToGameAppClient.TypeCase.SPSH_NAMED_MSG_VALS]: () => Promise.resolve(),
      [ToGameAppClient.TypeCase.SPSH_NAMED_VALS]: () => Promise.resolve(),
      [ToGameAppClient.TypeCase.SESSION_DATA]: async () => {
        this.handleSessionData(tgc);
      },
      [ToGameAppClient.TypeCase.TASK_START]: async () => {
        const msg = tgc.getTaskStart();
        console.log('[GameManager] Handling TASK_START for: ', msg);
        this.notifyStringMessageSubscribers(typeCase, msg?.toString() || '');
      },
      [ToGameAppClient.TypeCase.TASK_COMPLETE]: async () => {
        const msg = tgc.getTaskComplete();
        console.log('[GameManager] Handling TASK_COMPLETE for: ', msg);
        this.notifyStringMessageSubscribers(typeCase, msg?.toString() || '');
      },
      [ToGameAppClient.TypeCase.EXERCISE_START]: async () => {
        const msg = tgc.getExerciseStart();
        console.log('[GameManager] Handling EXERCISE_START for: ', msg);
        // this.notifyStringMessageSubscribers(typeCase, msg?.toString() || '');
      },
      [ToGameAppClient.TypeCase.SET_START]: async () => {
        const msg = tgc.getSetStart();
        console.log('[GameManager] Handling SET_START for: ', msg);
        // this.notifyStringMessageSubscribers(typeCase, msg?.toString() || '');
      },
      [ToGameAppClient.TypeCase.AUDIO_LISTEN_RESP]: async () => {
        const response = tgc.getAudioListenResp();
        console.log('[GameManager] Handling AUDIO_LISTEN_RESP: ', response);
        if (response) {
          this.notifyStringMessageSubscribers(typeCase, JSON.stringify({
            requestId: response.getRequestId(),
            success: response.getSuccess(),
            error: response.getError()
          }));
        }
      },
      [ToGameAppClient.TypeCase.ACTIVITY_INVOKE]: async () => {
        const activityInvoke = tgc.getActivityInvoke();
        console.log('[GameManager] Handling ACTIVITY_INVOKE: ', activityInvoke);
        if (activityInvoke) {
          this.notifyStringMessageSubscribers(typeCase, activityInvoke.getActivityId());
        }
      },
      [ToGameAppClient.TypeCase.IMAGE_EVAL_RESP]: async () => {
        const response = tgc.getImageEvalResp();
        console.log('[GameManager] Handling IMAGE_EVAL_RESP: ', response);
        if (response) {
          this.notifyStringMessageSubscribers(typeCase, JSON.stringify({
            requestId: response.getRequestId(),
            error: response.getError(),
            responseValue: response.getResponseValue()?.toObject()
          }));
        }
      },
      [ToGameAppClient.TypeCase.STATE_UPDATE_AS]: async () => {
        const msg = tgc.getStateUpdateAs();
        console.log('[GameManager] Handling STATE_UPDATE_AS for: ', msg);
        if (msg) {
          if (msg.getName() === 'chatModel') {
            const stateUpdate = {
              name: msg.getName(),
              values: msg.getValuesList()
            };
            this.notifyStringMessageSubscribers(typeCase, JSON.stringify(stateUpdate));
          }
        }
      },
    };

    if (handlers[typeCase]) {
      handlers[typeCase]().catch((error: Error) => {
        console.error('[GameManager] Error handling message:', error);
      });
    } else {
      console.warn('[GameManager] No handler for message type:', typeCase);
    }
  }

  private generateWorkoutSummary = (tgc: ToGameAppClient) => {
    const summary = ['<div class="workout-summary">'];

    const sessionData = tgc.getSessionData()!;
    const status = this.statusToString(sessionData.getStatus());
    summary.push(`<h3>Workout Status: ${status}</h3>`);

    sessionData.getExercisesList().forEach((exercise, exerciseIndex) => {
      const exerciseName = exercise.getName();
      summary.push(`<div class="exercise">
        <h4>Exercise ${exerciseIndex + 1}: ${exerciseName}</h4>
        <div class="sets">`);

      exercise.getSetsList().forEach((set, setIndex) => {
        const plannedReps = set.getPlannedReps();
        const actualReps = set.getActualReps();
        const status = this.statusToString(set.getStatus());
        const restTime = set.getRestTimeSeconds();
        summary.push(
          `<div class="set">
            Set ${setIndex + 1}: Planned ${plannedReps}, Actual ${actualReps}, Status: ${status}, Rest Time: ${restTime} seconds
          </div>`
        );
      });

      summary.push('</div></div>'); // Close sets and exercise divs
    });

    summary.push('</div>'); // Close workout-summary div
    return summary.join("\n");
  };

  private statusToString(status: Status): string {
    switch (status) {
      case Status.NOTSTARTED:
        return "Not Started";
      case Status.INPROGRESS:
        return "In Progress";
      case Status.COMPLETED:
        return "Completed";
      default:
        return "Unknown";
    }
  }

  private handleSessionData(tgc: ToGameAppClient): void {
    const sessionData = tgc.getSessionData()!;
    console.log('[GameManager] Received SessionData:', sessionData);

    const sessionDataString = JSON.stringify({
      status: sessionData.getStatus(),
      currentExerciseIndex: sessionData.getCurrentExerciseIndex(),
      exercises: sessionData.getExercisesList().map(exercise => ({
        name: exercise.getName(),
        currentSetIndex: exercise.getCurrentSetIndex(),
        sets: exercise.getSetsList().map(set => ({
          status: set.getStatus(),
          plannedReps: set.getPlannedReps(),
          actualReps: set.getActualReps()
        }))
      }))
    }, null, 2);

    const workoutSummary = this.generateWorkoutSummary(tgc);

    this.notifySessionDataSubscribers(workoutSummary);

    const sessionStatus = sessionData.getStatus();
    const currentExercise = sessionData.getExercisesList()[sessionData.getCurrentExerciseIndex()];
    const currentSet = currentExercise.getSetsList()[currentExercise.getCurrentSetIndex()];
    const currentExerciseIndex = sessionData.getCurrentExerciseIndex();
    const currentSetIndex = currentExercise.getCurrentSetIndex();

    // TODO: game would look at the session data and take appropriate action
    // for now, just make like we did the exercise and respond immediately
    // fill out currentSet and send it back
    if (sessionStatus === Status.INPROGRESS) {
      currentSet.setStatus(Status.COMPLETED);
      // update the actuals to equal the planned reps
      currentSet.setActualReps(currentSet.getPlannedReps());
    }

    // fill out the ExerciseSetReport
    const exerciseSetReport = new ExerciseSetReport();
    exerciseSetReport.setExerciseIndex(currentExerciseIndex);
    exerciseSetReport.setSetIndex(currentSetIndex);
    exerciseSetReport.setExerciseSet(currentSet);

    // wait 1 second
    setTimeout(() => {
      // send the updated currentSet back to the server
      this.send(new ToGameAppServer().setExerciseSetReport(exerciseSetReport));
    }, 1000);
  }

  private notifySessionDataSubscribers(data: string): void {
    this.sessionDataCallbacks.forEach(callback => {
      callback(data);
    });
  }

  public onSessionDataUpdate(callback: (data: string) => void): void {
    this.sessionDataCallbacks.push(callback);
  }

  private handleCommonAck(toClient: ToClient, ackType: 'LOGIN_ACK' | 'RESET_ACK', creqId: string, errMsg: string): void {
    const settlers = this.getAndRemoveSettlers(creqId);
    if (!settlers) {
      console.error('[GameManager] response w/o resolver', creqId, errMsg);
      return;
    }

    const { resolve, reject } = settlers;

    if (errMsg.length > 0) {
      console.error(`[GameManager] ${ackType} failed:`, errMsg);

      // Check for token-related errors
      if (errMsg.toLowerCase().includes('invalid token')) {
        // Dispatch event before rejecting the promise
        window.dispatchEvent(new CustomEvent('tokenExpired'));

        // Clean up the connection
        this.disconnect();

        // Clear the instance to allow re-initialization
        GameStreamingService.instance = null;
      }

      reject(new Error(errMsg));
      return;
    }

    console.log(`[GameManager] ${ackType} successful`, creqId);
    resolve(toClient);
  }

  private async handleLoginAckBase(toClient: ToClient): Promise<void> {
    const loginAck = toClient.getLoginAck()!;
    const errMsg = loginAck.getError();
    const creqId = loginAck.getCreqId();
    const appName = loginAck.getAppName();

    console.log(`[GameManager] LOGIN_ACK:`, creqId);
    console.log(`[GameManager] App Name:`, appName);

    if (creqId === this.loginCreqId && errMsg.length === 0) {
      console.log('[GameManager] Login successful, connection acknowledged');
      this.connAckd = true;
      this.loginCreqId = null;

      // Handle basic login acknowledgment first
      this.handleCommonAck(toClient, 'LOGIN_ACK', creqId, errMsg);

      // Store appName in auth store
      const authStore = useAuthStore();
      authStore.setAppName(appName);
      authStore.setWebAdminMode(loginAck.getWebAdminMode());

      // Store feature flags
      const featureFlagsStore = useFeatureFlagsStore();
      const featureFlags = loginAck.getFeatureFlagsList();
      console.log('[GameManager] Received feature flags:', featureFlags.sort().join(', '));
      featureFlagsStore.setFeatureFlags(featureFlags);

      if (appName === 'automab') {
        AutomabService.initialize(
          '/ws/automab',
          this.toast,
          this.userToken
        );
      }

      // Then handle audio setup via multiplexed connection
      const activeUserAudioMeetings = loginAck.getActiveUserAudioMeetingsList().map(uas => ({
        description: uas.getDescription(),
        meetingId: uas.getUserAudioMeetingId(),
        route: uas.getFeRoute() as '/chat' | '/meeting'
      }));

      await this.setupAudioConnection(activeUserAudioMeetings).catch(error => {
        console.error('[GameManager] Failed to establish audio connection:', error);
        this.toast.error('Failed to establish audio connection', {
          position: 'top-right',
          dismissible: true,
          duration: 3000,
        });
      });

      return;
    }

    this.handleCommonAck(toClient, 'LOGIN_ACK', creqId, errMsg);
  }

  private async setupAudioConnection(activeUserAudioMeetings: Meeting[]): Promise<void> {
    let connectionEstablished = false;
    while (!connectionEstablished) {
      try {
        console.log('[GameManager] Starting audio setup, active meetings:', activeUserAudioMeetings.length);
        // Check for stored route - only show join menu for /meeting
        const storedRoute = useAuthStore().originalRoute;
        console.log('[GameManager] Stored route:', storedRoute);
        // Default to new chat
        let selectedUserAudioMeetingId = '';
        let route: MeetingRoute = '/chat';
        if (storedRoute === '/meeting') {
          console.log('[GameManager] Showing join menu for meeting');
          const result = await new Promise<{ meetingId: string; route: MeetingRoute }>((resolve) => {
            window.dispatchEvent(new CustomEvent('showJoinMeeting', {
              detail: {
                meetings: activeUserAudioMeetings,
                onSelect: (meetingId: string, selectedRoute: MeetingRoute) => {
                  console.log('[GameManager] Meeting selected:', { meetingId, route: selectedRoute });
                  resolve({ meetingId, route: selectedRoute });
                }
              }
            }));
          });
          selectedUserAudioMeetingId = result.meetingId;
          route = result.route;
        }

        console.log('[GameManager] Initializing audio service with route:', route);
        // Initialize audio service with GameManager for multiplexed connection
        this.audioService = await RealtimeAudioStreamingService.initialize(
          this,
          this.toast,
          selectedUserAudioMeetingId,
          route
        );
        console.log('[GameManager] Audio service initialized via multiplexed connection');
        connectionEstablished = true;

        // If we get here, connection was successful
        console.log('[GameManager] Audio setup complete, dispatching navigation event with route:', route);
        window.dispatchEvent(new CustomEvent('readyForNavigation', {
          detail: { route }
        }));
      } catch (error) {
        console.error('[GameManager] Failed to establish audio connection:', error);
        this.toast.error('Failed to establish audio connection', {
          position: 'top-right',
          dismissible: true,
          duration: 3000
        });

        // If user cancels or starts new conversation, break the retry loop
        if (!activeUserAudioMeetings.length) {
          break;
        }
        // Otherwise, loop continues and shows join menu again
      }
    }
  }

  private handleResetAckBase(toClient: ToClient): void {
    const resetAck = toClient.getResetAck()!;
    const errMsg = resetAck.getError();
    const creqId = resetAck.getCreqId();
    console.log('[GameManager] RESET_ACK:', creqId);

    this.handleCommonAck(toClient, 'RESET_ACK', creqId, errMsg);
  }

  private notifyStringMessageSubscribers(pushCase: ToGameAppClient.TypeCase, message: string): void {
    this.stringMessageCallbacks.forEach(callback => {
      callback(pushCase, message);
    });
  }

  public onStringMessage(callback: (pushCase: ToGameAppClient.TypeCase, message: string) => void): void {
    this.stringMessageCallbacks.push(callback);
  }

  protected onCleanup(): void {
    for (const { timeout } of this.responseSettlers.values()) {
      clearTimeout(timeout);
    }
    this.responseSettlers.clear();
  }

  async send(tgs: ToGameAppServer): Promise<void> {
    // Validate that the message has content before sending
    if (tgs.getContentCase() === ToGameAppServer.ContentCase.CONTENT_NOT_SET) {
      console.error('[GameManager] Attempted to send empty ToGameAppServer message');
      return;
    }

    if (!this.isConnected()) {
      await this.connect();
    }

    const toServer = new ToServer().setGameMsg(tgs);
    this.wsConnection!.send(toServer.serializeBinary());
  }


}
